import axios from "axios";

/**
 * Backend base URL.
 *
 * The browser calls the FastAPI server directly (no dev-server proxy). CORS is
 * handled on the backend, which allows any http://localhost:* / http://127.0.0.1:*
 * origin in development.
 *
 * "localhost" is rewritten to "127.0.0.1" on purpose: on Windows, "localhost"
 * frequently resolves to the IPv6 address ::1, while uvicorn binds IPv4 only.
 * That mismatch shows up in the browser as ERR_CONNECTION_REFUSED even though
 * the server is clearly running.
 */
const RAW_URL = (
  process.env.REACT_APP_BACKEND_URL || "http://127.0.0.1:8000"
).trim();

export const BACKEND_URL = RAW_URL.replace(/\/+$/, "").replace(
  "//localhost:",
  "//127.0.0.1:"
);

export const API = BACKEND_URL.endsWith("/api")
  ? BACKEND_URL
  : `${BACKEND_URL}/api`;

if (process.env.NODE_ENV === "development") {
  // eslint-disable-next-line no-console
  console.info(`[api] calling backend directly at ${API}`);
}

/* Shared client. Slow endpoints override the timeout per call.
   A default timeout matters: without one axios waits forever, so a stalled
   request renders as an endless "Sending..." spinner instead of an error. */
const client = axios.create({ baseURL: API, timeout: 30000 });

/* Turn opaque axios failures into messages that actually say what went wrong. */
client.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      error.friendlyMessage =
        (data && (data.detail || data.message)) || `Server returned ${status}.`;
    } else if (error.code === "ECONNABORTED") {
      error.friendlyMessage = "The request timed out. Please try again.";
    } else {
      error.friendlyMessage =
        "Could not reach the server. Make sure the backend is running.";
      // eslint-disable-next-line no-console
      console.error(
        `[api] could not reach ${API} — start the backend from the backend/ folder:\n` +
          `      uvicorn server:app --reload --host 0.0.0.0 --port 8000`
      );
    }
    return Promise.reject(error);
  }
);

export const submitLead = (payload) => client.post("/leads", payload);

export const subscribeNewsletter = (payload) =>
  client.post("/newsletter", payload);

/* Run live SEO audit. PSI calls can take 20-30s, so timeout is generous. */
export const runSeoAudit = (payload) =>
  client.post("/seo-audit", payload, { timeout: 75000 }).then((r) => r.data);

/* Run live Shopify store audit (platform + conversion + marketing + SEO + speed). */
export const runShopifyAudit = (payload) =>
  client.post("/shopify-audit", payload, { timeout: 75000 }).then((r) => r.data);

/* Quick connectivity probe — call pingBackend() from the browser console. */
export const pingBackend = () => client.get("/").then((r) => r.data);

export default client;
